/**
 * What's New — In-app feature announcement module
 * v0.1.0
 *
 * Vanilla-JS IIFE — zero framework dependencies.
 * Loaded on Desk via app_include_js and on Website via web_include_js.
 *
 * Renders:
 *   1. A "What's New" pill in the Desk navbar (right side) with an unread badge.
 *      Falls back to a fixed top-right button when no navbar is found
 *      (Website / SPA contexts).
 *   2. A full-screen modal with two tabs (Released / Upcoming), a left feature
 *      index, and a right step-wise carousel.
 *
 * APIs consumed:
 *   - whats_new.api.list_announcements
 *   - whats_new.api.get_announcement
 *   - whats_new.api.mark_read
 */
(function () {
    'use strict';

    /* Guard — prevent double initialization */
    if (window.__WHATS_NEW__) return;
    window.__WHATS_NEW__ = true;

    /* ─────────────────────────────────────────────────────────────
     *  Helpers
     * ───────────────────────────────────────────────────────────── */
    function getCsrf() {
        if (window.frappe && window.frappe.csrf_token) return window.frappe.csrf_token;
        if (window.csrf_token && window.csrf_token !== 'Guest') return window.csrf_token;
        return '';
    }

    function getCurrentUser() {
        if (window.frappe && window.frappe.session && window.frappe.session.user) {
            return window.frappe.session.user;
        }
        if (window.frappe && window.frappe.boot && window.frappe.boot.user) {
            return window.frappe.boot.user.name || '';
        }
        // Fallback for non-Desk surfaces (FrappeCRM, custom Vue apps) where
        // `window.frappe` isn't exposed. Read the user_id cookie that Frappe
        // sets on login.
        try {
            var m = document.cookie.match(/(?:^|; )user_id=([^;]+)/);
            if (m) return decodeURIComponent(m[1]);
        } catch (e) { /* noop */ }
        return '';
    }

    function isGuest() {
        var u = getCurrentUser();
        return !u || u === 'Guest';
    }

    function escapeHtml(s) {
        if (s == null) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function formatDate(iso) {
        if (!iso) return '';
        var parts = String(iso).split('-');
        if (parts.length !== 3) return iso;
        var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var m = parseInt(parts[1], 10) - 1;
        return parts[2].replace(/^0/, '') + ' ' + (months[m] || parts[1]) + ' ' + parts[0];
    }

    function api(method, args) {
        args = args || {};
        var token = getCsrf();
        var fd = new URLSearchParams();
        Object.keys(args).forEach(function (k) { fd.append(k, args[k]); });

        return fetch('/api/method/' + method, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Frappe-CSRF-Token': token,
            },
            body: fd.toString(),
        }).then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
        }).then(function (json) {
            return json.message;
        });
    }

    /* ─────────────────────────────────────────────────────────────
     *  Styles
     * ───────────────────────────────────────────────────────────── */
    var STYLE_ID = 'whats-new-styles';
    function injectStyles() {
        if (document.getElementById(STYLE_ID)) return;
        var css = [
            '.wn-trigger { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: var(--wn-trigger-bg-image, var(--wn-trigger-bg, #7B61FF)); color: var(--wn-trigger-text, #fff); border: none; border-radius: 999px; font: 600 13px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,.08); transition: transform .12s, box-shadow .12s; position: relative; }',
            '.wn-trigger:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,0,0,.18); }',
            '.wn-trigger svg { width: 14px; height: 14px; }',
            '.wn-badge { display: none; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: #FF4757; color: #fff; font-size: 11px; font-weight: 700; align-items: center; justify-content: center; }',
            '.wn-badge.show { display: inline-flex; }',

            '.wn-backdrop { position: fixed; inset: 0; background: rgba(20, 18, 40, .55); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); z-index: 99990; opacity: 0; transition: opacity .2s; pointer-events: none; }',
            '.wn-backdrop.open { opacity: 1; pointer-events: auto; }',

            '.wn-modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(.97); z-index: 99999; width: min(1080px, 94vw); height: min(720px, 88vh); background: #fff; border-radius: 16px; box-shadow: 0 24px 60px rgba(0,0,0,.28); display: flex; flex-direction: column; overflow: hidden; opacity: 0; transition: opacity .2s, transform .2s; pointer-events: none; font: 14px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif; color: #1A1B2E; }',
            '.wn-modal.open { opacity: 1; transform: translate(-50%, -50%) scale(1); pointer-events: auto; }',

            '.wn-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px 12px; border-bottom: 1px solid #ECEEF5; }',
            '.wn-title-block { display: flex; flex-direction: column; gap: 2px; }',
            '.wn-title-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }',
            '.wn-title { font-size: 18px; font-weight: 700; letter-spacing: -.01em; display: inline-flex; align-items: center; gap: 6px; }',
            '.wn-sparkle { color: #7B61FF; }',
            '.wn-latest { font-size: 12px; color: #6B6F86; }',
            '.wn-subtitle { font-size: 12px; color: #6B6F86; margin-top: 1px; }',
            '.wn-close { width: 32px; height: 32px; border-radius: 8px; border: none; background: transparent; color: #6B6F86; cursor: pointer; font-size: 18px; display: inline-flex; align-items: center; justify-content: center; }',
            '.wn-close:hover { background: #F2F3F8; color: #1A1B2E; }',

            '.wn-tabs { display: flex; gap: 4px; padding: 8px 22px 0; border-bottom: 1px solid #ECEEF5; background: #FAFBFE; }',
            '.wn-tab { padding: 10px 16px; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; font: 600 13px/1 inherit; color: #6B6F86; transition: color .12s, border-color .12s; }',
            '.wn-tab.active { color: #7B61FF; border-bottom-color: #7B61FF; }',
            '.wn-tab:hover:not(.active) { color: #1A1B2E; }',

            '.wn-body { flex: 1; display: flex; min-height: 0; }',
            '.wn-index { width: 38%; min-width: 240px; max-width: 360px; border-right: 1px solid #ECEEF5; overflow-y: auto; padding: 12px 8px; background: #FAFBFE; }',
            '.wn-detail { flex: 1; display: flex; flex-direction: column; overflow: hidden; }',

            '.wn-card { display: block; width: 100%; text-align: left; padding: 12px 12px; margin-bottom: 6px; border: 1px solid transparent; border-radius: 10px; background: transparent; cursor: pointer; transition: background .12s, border-color .12s; }',
            '.wn-card:hover { background: #fff; border-color: #ECEEF5; }',
            '.wn-card.active { background: #fff; border-color: #DDD3FF; box-shadow: 0 1px 2px rgba(123,97,255,.08); position: relative; }',
            '.wn-card.active::before { content: ""; position: absolute; left: -4px; top: 10px; bottom: 10px; width: 3px; border-radius: 2px; background: #7B61FF; }',
            '.wn-card-meta { font-size: 11px; color: #8B8FA8; font-weight: 600; display: flex; gap: 8px; align-items: center; }',
            '.wn-card-version { background: var(--wn-version-bg, #7B61FF); color: var(--wn-version-text, #fff); padding: 2px 8px; border-radius: 999px; font-size: 10px; letter-spacing: .04em; }',
            '.wn-card-title { font-size: 13px; font-weight: 600; color: #1A1B2E; margin-top: 6px; line-height: 1.35; }',
            '.wn-card-dot { width: 8px; height: 8px; border-radius: 50%; background: #FF4757; flex-shrink: 0; }',

            '.wn-detail-scroll { flex: 1; overflow-y: auto; padding: 24px 32px; }',
            '.wn-chip { display: inline-block; padding: 4px 10px; border-radius: 999px; background: #E8FAEC; color: #1A8C3A; font: 700 11px/1 inherit; letter-spacing: .08em; }',
            '.wn-detail-meta { font-size: 11px; color: #8B8FA8; font-weight: 600; margin-left: 8px; }',
            '.wn-detail-title { font-size: 24px; font-weight: 700; letter-spacing: -.015em; margin: 10px 0 14px; }',
            '.wn-summary { margin: 0 0 14px; color: #2E3146; font-size: 15px; line-height: 1.55; font-weight: 500; white-space: pre-line; }',
            '.wn-feature-video { display: inline-flex; align-items: center; gap: 8px; margin: 0 0 18px; padding: 7px 14px 7px 11px; border-radius: 999px; background: var(--wn-fv-bg-image, var(--wn-fv-bg, linear-gradient(135deg, #FF6B7A 0%, #FF3D55 100%))); color: var(--wn-fv-text, #fff); font-weight: 600; font-size: 12.5px; text-decoration: none; box-shadow: 0 2px 8px rgba(255, 61, 85, 0.28); transition: transform .12s, box-shadow .12s; }',
            '.wn-feature-video:hover { color: var(--wn-fv-text, #fff); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(255, 61, 85, 0.4); text-decoration: none; }',
            '.wn-feature-video svg { width: 14px; height: 14px; flex-shrink: 0; }',
            '.wn-screenshot { border: 1px solid #ECEEF5; border-radius: 12px; overflow: hidden; background: #F7F8FC; }',
            '.wn-screenshot img { display: block; width: 100%; max-height: 360px; object-fit: cover; object-position: top; }',
            '.wn-screenshot-empty { padding: 60px 20px; text-align: center; color: #B0B3C7; font-size: 13px; }',
            '.wn-step-description { margin: 18px 0 12px; color: #4A4D60; font-size: 14px; line-height: 1.6; white-space: pre-line; }',
            '.wn-bullets { list-style: none; padding: 0; margin: 0 0 12px; }',
            '.wn-bullets li { display: flex; align-items: flex-start; gap: 8px; padding: 5px 0; font-size: 13px; color: #1A1B2E; }',
            '.wn-bullets li::before { content: "\\2713"; color: #28C840; font-weight: 700; flex-shrink: 0; }',
            '.wn-video { margin-top: 10px; }',
            '.wn-video a { color: #7B61FF; font-weight: 600; font-size: 13px; }',

            '.wn-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 32px; border-top: 1px solid #ECEEF5; background: #FAFBFE; }',
            '.wn-step-counter { font-size: 11px; color: #8B8FA8; font-weight: 600; letter-spacing: .06em; }',
            '.wn-nav { display: flex; gap: 10px; }',
            '.wn-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px; border: 1px solid #E2E4ED; border-radius: 10px; background: #fff; cursor: pointer; font: 600 13px/1 inherit; color: #1A1B2E; transition: background .12s, opacity .12s; }',
            '.wn-btn:hover:not(:disabled):not(.wn-btn-primary) { background: #F2F3F8; }',
            '.wn-btn:disabled { opacity: .4; cursor: not-allowed; }',
            '.wn-btn-primary { background: var(--wn-cta-bg-image, var(--wn-cta-bg, linear-gradient(135deg, #6FE6C4 0%, #7B61FF 60%, #FF7E8B 100%))); color: var(--wn-cta-text, #fff); border: none; padding: 10px 22px; }',
            '.wn-btn-primary:hover:not(:disabled) { background: var(--wn-cta-bg-image, var(--wn-cta-bg, linear-gradient(135deg, #6FE6C4 0%, #7B61FF 60%, #FF7E8B 100%))); filter: brightness(1.08); box-shadow: 0 4px 14px rgba(0, 0, 0, .22); }',

            '.wn-empty { padding: 60px 20px; text-align: center; color: #8B8FA8; font-size: 14px; }',

            /* ── Module taxonomy: filter pill row + per-card chip + detail chip ── */
            '.wn-modfilter { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 22px 10px; border-bottom: 1px solid #ECEEF5; background: #FAFBFE; }',
            '.wn-modfilter-pill { --wn-mc: #B0B3C7; display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--wn-mc); background: var(--wn-mc); color: #fff; font: 600 11px/1 inherit; cursor: pointer; opacity: .55; transition: opacity .12s, box-shadow .12s; }',
            '.wn-modfilter-pill:hover { opacity: .8; }',
            '.wn-modfilter-pill.active { opacity: 1; box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--wn-mc); }',
            '.wn-modfilter-pill.wn-modfilter-all { --wn-mc: #1A1B2E; }',
            '.wn-card-module { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px; font: 700 10px/1 inherit; letter-spacing: .04em; color: #fff; }',
            '.wn-detail-module { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font: 700 11px/1 inherit; letter-spacing: .06em; color: #fff; margin-right: 6px; }',

            /* ── NAV-03 subscribed-state CTA (Upcoming final step) ── */
            '.wn-btn-subscribed { background: #E8FAEC; color: #1A8C3A; border-color: #B7EFC2; }',
            '.wn-btn-subscribed:hover:not(:disabled) { background: #D6F4DD; }',

            /* ── Phase-2 mobile responsive ── */
            '@media (max-width: 1023px) and (min-width: 768px) { .wn-index { min-width: 200px; width: 32%; } .wn-detail-scroll { padding: 20px 22px; } }',
            '@media (max-width: 767px) { ' +
                '.wn-modal { width: 100vw; height: 100vh; border-radius: 0; top: 0; left: 0; transform: none; } ' +
                '.wn-modal.open { transform: none; } ' +
                '.wn-header { padding: 14px 16px 10px; } ' +
                '.wn-title { font-size: 16px; } ' +
                '.wn-tabs, .wn-modfilter { padding-left: 16px; padding-right: 16px; } ' +
                '.wn-body { flex-direction: column; } ' +
                '.wn-index { width: 100%; max-width: none; max-height: 38vh; border-right: none; border-bottom: 1px solid #ECEEF5; } ' +
                '.wn-detail-scroll { padding: 18px 16px; } ' +
                '.wn-detail-title { font-size: 20px; } ' +
                '.wn-screenshot img { max-height: 44vh; } ' +
                '.wn-footer { padding: 12px 16px; gap: 8px; flex-wrap: wrap; } ' +
                '.wn-btn { padding: 10px 14px; min-height: 40px; } ' +
                '.wn-btn-primary { padding: 10px 16px; } ' +
            '}',
        ].join('\n');

        var style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = css;
        document.head.appendChild(style);
    }

    /* ─────────────────────────────────────────────────────────────
     *  State
     * ───────────────────────────────────────────────────────────── */
    var state = {
        released: [],
        upcoming: [],
        announcements: [],      // image-only entries (no step walkthrough)
        modules: [],            // [{name, color, icon, sort_order}, ...]
        moduleFilter: '',       // '' = All; otherwise module name
        unreadCount: 0,
        activeTab: 'released',
        activeName: null,
        activeStepIdx: 0,
        currentDetail: null,
        latestLabel: '',
    };
    var MODULE_FILTER_KEY = 'wn_module_filter';
    var triggerEl = null;
    var badgeEls = new Set();
    var backdropEl = null;
    var modalEl = null;
    var stylesInjected = false;
    var dataLoadedOnce = false;

    /* ─────────────────────────────────────────────────────────────
     *  Trigger button (navbar pill OR fixed FAB fallback)
     * ───────────────────────────────────────────────────────────── */
    function buildTrigger() {
        var btn = document.createElement('button');
        btn.className = 'wn-trigger';
        btn.type = 'button';
        btn.setAttribute('aria-label', "What's New");
        btn.innerHTML = (
            '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M12 1l2.39 6.36L21 8.27l-5 4.87L17.18 21 12 17.77 6.82 21 8 13.14l-5-4.87 6.61-.91L12 1z"/>' +
            '</svg>' +
            '<span>What’s New</span>' +
            '<span class="wn-badge" data-wn-badge></span>'
        );
        btn.addEventListener('click', openModal);
        return btn;
    }

    function _registerTrigger(el) {
        var badge = el.querySelector('[data-wn-badge]');
        if (badge) {
            badgeEls.add(badge);
            _paintBadge(badge, state.unreadCount);
        }
    }

    function _paintBadge(el, n) {
        if (n > 0) {
            el.textContent = n > 99 ? '99+' : String(n);
            el.classList.add('show');
        } else {
            el.textContent = '';
            el.classList.remove('show');
        }
    }

    function mountTriggerInto(target) {
        if (!target) return null;
        var existing = target.querySelector(':scope > .wn-trigger');
        if (existing) return existing;
        var el = buildTrigger();
        target.appendChild(el);
        _registerTrigger(el);
        return el;
    }

    function _isCrmRoute() {
        return location && location.pathname && location.pathname.indexOf('/crm') === 0;
    }

    function mountTrigger() {
        // CRM consumers mount explicitly via window.WhatsNew.mount(target);
        // skip the auto FAB fallback there so the corner stays clean.
        if (_isCrmRoute()) return;
        if (triggerEl && triggerEl.isConnected) return;
        triggerEl = buildTrigger();
        _registerTrigger(triggerEl);

        // Desk navbar — try multiple selectors for resilience across versions.
        var navHost = (
            document.querySelector('.navbar .navbar-right') ||
            document.querySelector('header .navbar-nav.navbar-right') ||
            document.querySelector('.navbar-collapse .navbar-nav.ms-auto') ||
            document.querySelector('.navbar .navbar-nav:last-child')
        );

        if (navHost) {
            var li = document.createElement('li');
            li.className = 'nav-item';
            li.style.cssText = 'display: flex; align-items: center; padding: 0 8px;';
            li.appendChild(triggerEl);
            navHost.insertBefore(li, navHost.firstChild);
        }
        // No fallback: BRD scopes the pill to the ERP nav bar only.
        // Non-Desk surfaces (CRM, embedded toolbars) mount explicitly
        // via window.WhatsNew.mount(target).
    }

    function setBadge(n) {
        state.unreadCount = n || 0;
        badgeEls.forEach(function (el) {
            if (!el.isConnected) { badgeEls.delete(el); return; }
            _paintBadge(el, state.unreadCount);
        });
    }

    /* ─────────────────────────────────────────────────────────────
     *  Data loading
     * ───────────────────────────────────────────────────────────── */
    function loadList() {
        return api('whats_new.api.list_announcements').then(function (data) {
            if (!data) return;
            state.released = data.released || [];
            state.upcoming = data.upcoming || [];
            state.announcements = data.announcements || [];
            state.modules = data.modules || [];
            // Restore persisted filter — drop it if module no longer present.
            try {
                var saved = localStorage.getItem(MODULE_FILTER_KEY) || '';
                var avail = (state.modules || []).map(function (m) { return m.name; });
                state.moduleFilter = (saved && avail.indexOf(saved) !== -1) ? saved : '';
            } catch (e) { state.moduleFilter = ''; }
            setBadge(data.unread_count || 0);
            if (state.released.length) {
                var top = state.released[0];
                state.latestLabel = 'Latest: ' + (top.version ? top.version + ' — ' : '') + formatDate(top.release_date);
            }
            return data;
        });
    }

    function loadDetail(name) {
        return api('whats_new.api.get_announcement', { name: name }).then(function (data) {
            state.currentDetail = data || null;
            state.activeStepIdx = 0;
            return data;
        });
    }

    function sendMarkRead(name) {
        return api('whats_new.api.mark_read', { announcement: name }).then(function (data) {
            if (data && typeof data.unread_count === 'number') {
                setBadge(data.unread_count);
            }
            // Locally flag the entry as read so the index reflects immediately.
            // Patch both tabs — Upcoming features can also be acknowledged now.
            (state.released || []).forEach(function (it) {
                if (it.name === name) it.is_read = true;
            });
            (state.upcoming || []).forEach(function (it) {
                if (it.name === name) it.is_read = true;
            });
            return data;
        });
    }

    /* ─────────────────────────────────────────────────────────────
     *  Modal — build, render, navigate
     * ───────────────────────────────────────────────────────────── */
    function buildModal() {
        backdropEl = document.createElement('div');
        backdropEl.className = 'wn-backdrop';
        backdropEl.addEventListener('click', closeModal);

        modalEl = document.createElement('div');
        modalEl.className = 'wn-modal';
        modalEl.setAttribute('role', 'dialog');
        modalEl.setAttribute('aria-modal', 'true');

        modalEl.innerHTML = (
            '<div class="wn-header">' +
                '<div class="wn-title-block">' +
                    '<div class="wn-title-row">' +
                        '<div class="wn-title"><span class="wn-sparkle">✨</span> What’s New</div>' +
                        '<div class="wn-latest" data-wn-latest></div>' +
                    '</div>' +
                    '<div class="wn-subtitle">Recently shipped &amp; what’s coming next</div>' +
                '</div>' +
                '<button class="wn-close" type="button" aria-label="Close">×</button>' +
            '</div>' +
            '<div class="wn-tabs">' +
                '<button class="wn-tab active" type="button" data-wn-tab="released">Released</button>' +
                '<button class="wn-tab" type="button" data-wn-tab="upcoming">Upcoming</button>' +
                '<button class="wn-tab" type="button" data-wn-tab="announcement">Announcements</button>' +
            '</div>' +
            '<div class="wn-modfilter" data-wn-modfilter></div>' +
            '<div class="wn-body">' +
                '<div class="wn-index" data-wn-index></div>' +
                '<div class="wn-detail">' +
                    '<div class="wn-detail-scroll" data-wn-detail></div>' +
                    '<div class="wn-footer">' +
                        '<div class="wn-step-counter" data-wn-counter></div>' +
                        '<div class="wn-nav">' +
                            '<button class="wn-btn" type="button" data-wn-prev>← Previous</button>' +
                            '<button class="wn-btn wn-btn-primary" type="button" data-wn-next>Next →</button>' +
                        '</div>' +
                    '</div>' +
                '</div>' +
            '</div>'
        );

        modalEl.querySelector('.wn-close').addEventListener('click', closeModal);
        modalEl.querySelectorAll('[data-wn-tab]').forEach(function (b) {
            b.addEventListener('click', function () { setTab(b.getAttribute('data-wn-tab')); });
        });
        modalEl.querySelector('[data-wn-prev]').addEventListener('click', prevStep);
        modalEl.querySelector('[data-wn-next]').addEventListener('click', nextStep);

        document.body.appendChild(backdropEl);
        document.body.appendChild(modalEl);

        document.addEventListener('keydown', onKeyDown);
    }

    function onKeyDown(e) {
        if (!modalEl || !modalEl.classList.contains('open')) return;
        if (e.key === 'Escape') closeModal();
        if (e.key === 'ArrowRight') nextStep();
        if (e.key === 'ArrowLeft') prevStep();
    }

    function setTab(tab) {
        state.activeTab = tab;
        state.activeName = null;
        state.activeStepIdx = 0;
        state.currentDetail = null;
        modalEl.querySelectorAll('[data-wn-tab]').forEach(function (b) {
            b.classList.toggle('active', b.getAttribute('data-wn-tab') === tab);
        });
        renderModuleFilter();
        var list = _filteredList(tab);
        if (list.length) {
            selectAnnouncement(list[0].name);
        } else {
            renderIndex();
            renderDetail(null);
        }
    }

    function _filteredList(tab) {
        var base;
        if (tab === 'released') base = state.released;
        else if (tab === 'announcement') base = state.announcements;
        else base = state.upcoming;
        if (!state.moduleFilter) return base;
        return base.filter(function (it) { return it.module === state.moduleFilter; });
    }

    function _nextAnnouncementName() {
        var list = _filteredList(state.activeTab);
        if (!list.length || !state.activeName) return null;
        var idx = list.findIndex(function (it) { return it.name === state.activeName; });
        if (idx === -1 || idx + 1 >= list.length) return null;
        return list[idx + 1].name;
    }

    function setModuleFilter(name) {
        state.moduleFilter = name || '';
        try { localStorage.setItem(MODULE_FILTER_KEY, state.moduleFilter); } catch (e) {}
        renderModuleFilter();
        var list = _filteredList(state.activeTab);
        if (list.length) selectAnnouncement(list[0].name);
        else { renderIndex(); renderDetail(null); }
    }

    function renderModuleFilter() {
        var host = modalEl.querySelector('[data-wn-modfilter]');
        if (!host) return;
        if (!(state.modules || []).length) { host.innerHTML = ''; host.style.display = 'none'; return; }
        host.style.display = '';
        var pills = ['<button class="wn-modfilter-pill wn-modfilter-all ' + (!state.moduleFilter ? 'active' : '') +
                     '" type="button" data-wn-mod="">All</button>'];
        (state.modules || []).forEach(function (m) {
            var color = m.color || '#B0B3C7';
            var ico = m.icon ? (escapeHtml(m.icon) + ' ') : '';
            pills.push(
                '<button class="wn-modfilter-pill ' + (state.moduleFilter === m.name ? 'active' : '') +
                '" type="button" data-wn-mod="' + escapeHtml(m.name) + '"' +
                ' style="--wn-mc:' + escapeHtml(color) + '">' +
                    ico + escapeHtml(m.name) +
                '</button>'
            );
        });
        host.innerHTML = pills.join('');
        host.querySelectorAll('[data-wn-mod]').forEach(function (el) {
            el.addEventListener('click', function () { setModuleFilter(el.getAttribute('data-wn-mod')); });
        });
    }

    function selectAnnouncement(name) {
        state.activeName = name;
        renderIndex();
        loadDetail(name).then(function () {
            renderDetail(state.currentDetail);
        });
    }

    function renderIndex() {
        var hostIndex = modalEl.querySelector('[data-wn-index]');
        var list = _filteredList(state.activeTab);
        if (!list.length) {
            hostIndex.innerHTML = '<div class="wn-empty">Nothing here yet.</div>';
            return;
        }
        var html = list.map(function (item) {
            var dateLabel = state.activeTab === 'upcoming'
                ? ('Expected ' + formatDate(item.expected_date))
                : formatDate(item.release_date);
            var dot = (state.activeTab !== 'upcoming' && !item.is_read)
                ? '<span class="wn-card-dot" aria-label="Unread"></span>'
                : '';
            var versionPill = item.version
                ? '<span class="wn-card-version">' + escapeHtml(item.version) + '</span>'
                : '';
            var moduleChip = item.module
                ? '<span class="wn-card-module" style="background:' + (item.module_color || '#7B61FF') + '">' +
                      (item.module_icon ? escapeHtml(item.module_icon) + ' ' : '') +
                      escapeHtml(item.module) +
                  '</span>'
                : '';
            return (
                '<button class="wn-card ' + (item.name === state.activeName ? 'active' : '') +
                '" type="button" data-wn-select="' + escapeHtml(item.name) + '">' +
                    '<div class="wn-card-meta">' +
                        versionPill +
                        moduleChip +
                        '<span>' + escapeHtml(dateLabel) + '</span>' +
                        dot +
                    '</div>' +
                    '<div class="wn-card-title">' + escapeHtml(item.title) + '</div>' +
                '</button>'
            );
        }).join('');
        hostIndex.innerHTML = html;
        hostIndex.querySelectorAll('[data-wn-select]').forEach(function (el) {
            el.addEventListener('click', function () {
                selectAnnouncement(el.getAttribute('data-wn-select'));
            });
        });
    }

    function renderDetail(detail) {
        var host = modalEl.querySelector('[data-wn-detail]');
        var counterEl = modalEl.querySelector('[data-wn-counter]');
        var prevBtn = modalEl.querySelector('[data-wn-prev]');
        var nextBtn = modalEl.querySelector('[data-wn-next]');
        modalEl.querySelector('[data-wn-latest]').textContent = state.latestLabel || '';

        if (!detail) {
            host.innerHTML = '<div class="wn-empty">Select a feature on the left to see details.</div>';
            counterEl.textContent = '';
            prevBtn.style.display = 'none';
            nextBtn.style.display = 'none';
            return;
        }

        prevBtn.style.display = '';
        nextBtn.style.display = '';

        var steps = detail.steps || [];
        // The "Announcement" type is image-only — it legitimately has zero
        // steps, so it renders the single About slide (with its image) below.
        // Only Released/Upcoming with no steps are treated as misconfigured.
        if (!steps.length && detail.announcement_type !== 'Announcement') {
            host.innerHTML = '<div class="wn-empty">No steps configured for this feature.</div>';
            counterEl.textContent = '';
            return;
        }

        // Slide model: slide 0 = "About" (description + feature video only);
        // slides 1..N = the existing per-step content. Total slides = steps.length + 1.
        var totalSlides = steps.length + 1;
        var slideIdx = state.activeStepIdx;
        if (slideIdx < 0) slideIdx = 0;
        if (slideIdx > totalSlides - 1) slideIdx = totalSlides - 1;
        var isAbout = slideIdx === 0;
        var isLast = slideIdx === totalSlides - 1;

        var dateLabel;
        if (detail.announcement_type === 'Upcoming') {
            dateLabel = 'Expected: ' + formatDate(detail.expected_date);
        } else if (detail.announcement_type === 'Announcement') {
            dateLabel = formatDate(detail.release_date);
        } else {
            dateLabel = 'Released: ' + formatDate(detail.release_date);
        }

        var moduleChip = detail.module
            ? '<span class="wn-detail-module" style="background:' + (detail.module_color || '#7B61FF') + '">' +
                  (detail.module_icon ? escapeHtml(detail.module_icon) + ' ' : '') +
                  escapeHtml(detail.module) +
              '</span>'
            : '';

        var metaRow = moduleChip +
            '<span class="wn-chip">' + escapeHtml((detail.category || 'UPDATE').toUpperCase()) + '</span>' +
            '<span class="wn-detail-meta">' + escapeHtml((detail.version || '') + (detail.version ? ' · ' : '') + dateLabel) + '</span>';

        if (isAbout) {
            // About slide — description + Watch-feature-video button only.
            // Screenshot / step description / bullets / per-step video are NOT
            // shown here (those live on the Know More slides).
            var summaryHtml = detail.summary_description
                ? '<p class="wn-summary">' + escapeHtml(detail.summary_description) + '</p>'
                : '<p class="wn-summary" style="color:#8B8FA8;font-style:italic;">No description yet — ask the author to add one.</p>';

            var featureVideoHtml = detail.feature_video_url
                ? '<a class="wn-feature-video" href="' + escapeHtml(detail.feature_video_url) + '" target="_blank" rel="noopener" aria-label="' + escapeHtml(featureVideoLabel) + '"' + (detail.image ? ' style="margin-top:16px"' : '') + '>' +
                      '<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M3 2.5v11a.5.5 0 0 0 .77.42l9-5.5a.5.5 0 0 0 0-.84l-9-5.5A.5.5 0 0 0 3 2.5z"/></svg>' +
                      '<span>' + escapeHtml(featureVideoLabel) + '</span>' +
                  '</a>'
                : '';

            // Announcement-type image — single image rendered on the About
            // slide when set (image-only entries with no step walkthrough).
            var imageHtml = detail.image
                ? '<div class="wn-screenshot" style="margin-top:14px"><img src="' + escapeHtml(detail.image) + '" alt=""></div>'
                : '';

            host.innerHTML = (
                '<div>' +
                    metaRow +
                    '<h2 class="wn-detail-title">' + escapeHtml(detail.title || '') + '</h2>' +
                    summaryHtml +
                    imageHtml +
                    featureVideoHtml +
                '</div>'
            );
        } else {
            // Step slide — process screenshots / bullets / per-step copy.
            // Announcement-level summary + feature-video button are deliberately
            // suppressed here (About slide owns them).
            var step = steps[slideIdx - 1] || steps[0];

            var screenshotHtml = step.screenshot
                ? '<div class="wn-screenshot"><img src="' + escapeHtml(step.screenshot) + '" alt=""></div>'
                : '<div class="wn-screenshot"><div class="wn-screenshot-empty">No screenshot for this step.</div></div>';

            var bulletsHtml = (step.bullet_highlights && step.bullet_highlights.length)
                ? '<ul class="wn-bullets">' + step.bullet_highlights.map(function (b) {
                      return '<li>' + escapeHtml(b) + '</li>';
                  }).join('') + '</ul>'
                : '';

            var videoHtml = step.video_url
                ? '<div class="wn-video"><a href="' + escapeHtml(step.video_url) + '" target="_blank" rel="noopener">Watch the walkthrough →</a></div>'
                : '';

            host.innerHTML = (
                '<div>' +
                    metaRow +
                    '<h2 class="wn-detail-title">' + escapeHtml(step.step_title || detail.title) + '</h2>' +
                    screenshotHtml +
                    '<p class="wn-step-description">' + escapeHtml(step.step_description || '') + '</p>' +
                    bulletsHtml +
                    videoHtml +
                '</div>'
            );
        }

        // Counter: About on slide 0, Step N of M on subsequent slides.
        if (isAbout) {
            counterEl.textContent = steps.length ? 'About' : 'About this feature';
        } else {
            counterEl.textContent = 'Step ' + slideIdx + ' of ' + steps.length;
        }
        prevBtn.disabled = slideIdx === 0;

        // "Ok, Got It!" stays active on every open — server-side `read_count`
        // increments per click, so repeat acknowledgements are tracked instead
        // of being suppressed by client state.
        nextBtn.classList.remove('wn-btn-subscribed');
        nextBtn.disabled = false;
        if (isLast) {
            // On the last step: advance to the next entry in the list when one
            // exists ("Next Feature →"); only the final entry closes the modal
            // ("Ok, Got It!"). Either way the click records the read (nextStep).
            nextBtn.textContent = _nextAnnouncementName() ? 'Next Feature →' : 'Ok, Got It!';
        } else if (isAbout) {
            nextBtn.textContent = 'Know More →';
        } else {
            nextBtn.textContent = 'Next →';
        }
    }

    function prevStep() {
        if (!state.currentDetail) return;
        if (state.activeStepIdx > 0) {
            state.activeStepIdx -= 1;
            renderDetail(state.currentDetail);
        }
    }

    function nextStep() {
        if (!state.currentDetail) return;
        var steps = state.currentDetail.steps || [];
        var totalSlides = steps.length + 1; // About slide + N step slides
        if (state.activeStepIdx < totalSlides - 1) {
            state.activeStepIdx += 1;
            renderDetail(state.currentDetail);
            return;
        }
        // Final slide → acknowledge, then either advance to the next entry in
        // the list ("Next Feature →") or, when this is the last entry, close
        // ("Ok, Got It!"). Always re-acknowledge — server increments read_count
        // per click. Idempotent for the badge.
        var detail = state.currentDetail;
        var name = detail.name;
        var nextName = _nextAnnouncementName();
        var calls = [sendMarkRead(name)];
        if (detail.announcement_type === 'Upcoming') {
            calls.push(subscribeReminder(name).catch(function () {
                /* tolerate — read is the primary acknowledgement */
            }));
        }
        var advanceOrClose = function () {
            if (nextName) {
                // selectAnnouncement → loadDetail resets activeStepIdx to 0,
                // so the next entry opens on its About slide.
                selectAnnouncement(nextName);
            } else {
                setTimeout(closeModal, 200);
            }
        };
        Promise.all(calls).then(advanceOrClose).catch(advanceOrClose);
    }

    function subscribeReminder(name) {
        return api('whats_new.reminders.subscribe_reminder', { announcement: name });
    }

    function _hashTarget() {
        try {
            var h = String(location.hash || '');
            var m = h.match(/whats-new=([A-Za-z0-9_\-]+)/);
            return m ? m[1] : '';
        } catch (e) { return ''; }
    }

    function openModal() {
        if (!modalEl) buildModal();
        loadList().then(function () {
            renderModuleFilter();
            // Deep link via URL hash: #whats-new=WN-0001 (used by reminder Notification Log)
            var hashTarget = _hashTarget();
            if (hashTarget) {
                // Search both tabs; activate the matching tab if found.
                var found = (state.released || []).find(function (r) { return r.name === hashTarget; });
                if (found) state.activeTab = 'released';
                if (!found) {
                    found = (state.upcoming || []).find(function (r) { return r.name === hashTarget; });
                    if (found) state.activeTab = 'upcoming';
                }
                if (!found) {
                    found = (state.announcements || []).find(function (r) { return r.name === hashTarget; });
                    if (found) state.activeTab = 'announcement';
                }
                if (found) {
                    modalEl.querySelectorAll('[data-wn-tab]').forEach(function (b) {
                        b.classList.toggle('active', b.getAttribute('data-wn-tab') === state.activeTab);
                    });
                    selectAnnouncement(found.name);
                    backdropEl.classList.add('open');
                    modalEl.classList.add('open');
                    return;
                }
            }
            var list = _filteredList(state.activeTab);
            if (list.length) {
                selectAnnouncement(list[0].name);
            } else {
                renderIndex();
                renderDetail(null);
            }
            backdropEl.classList.add('open');
            modalEl.classList.add('open');
        });
    }

    function closeModal() {
        if (!modalEl) return;
        backdropEl.classList.remove('open');
        modalEl.classList.remove('open');
    }

    /* ─────────────────────────────────────────────────────────────
     *  Boot
     * ───────────────────────────────────────────────────────────── */
    function ensureInit() {
        if (isGuest()) return false;
        if (!stylesInjected) {
            injectStyles();
            stylesInjected = true;
        }
        if (!dataLoadedOnce) {
            dataLoadedOnce = true;
            loadList();
        }
        return true;
    }

    // Per-surface visibility flags + theme colours fetched from Whats New
    // Settings. The bundle still exposes window.WhatsNew.mount so explicit
    // consumers work regardless — flags only gate the AUTO-mount on Desk.
    var visibility = { show_on_desk: true, show_on_frappe_crm: true };
    var visibilityFetched = null;
    // Resolved feature-video pill label — set from theme.feature_video_label
    // via applyTheme(), read by renderDetail() when emitting the About-slide
    // pill. Fallback string keeps the pill labelled even before theme arrives.
    var featureVideoLabel = 'Watch feature video';
    function applyTheme(theme) {
        if (!theme || typeof theme !== 'object') return;
        var root = document.documentElement;
        // CSS variables on <html> so they cascade to every .wn-* element,
        // including the trigger pill (mounted on the navbar) AND the modal
        // body (teleported under <body>).
        if (theme.trigger_pill_bg) root.style.setProperty('--wn-trigger-bg', theme.trigger_pill_bg);
        if (theme.trigger_pill_text) root.style.setProperty('--wn-trigger-text', theme.trigger_pill_text);
        // Gradient / preset: server pre-builds the full `linear-gradient(...)`
        // CSS string. Empty string → Solid style → clear so the solid
        // --wn-trigger-bg fallback wins.
        if (theme.trigger_pill_image) {
            root.style.setProperty('--wn-trigger-bg-image', theme.trigger_pill_image);
        } else {
            root.style.removeProperty('--wn-trigger-bg-image');
        }
        if (theme.version_pill_bg) root.style.setProperty('--wn-version-bg', theme.version_pill_bg);
        if (theme.version_pill_text) root.style.setProperty('--wn-version-text', theme.version_pill_text);
        // Modal CTA button — same fallback dance as the trigger pill.
        if (theme.cta_button_bg) root.style.setProperty('--wn-cta-bg', theme.cta_button_bg);
        if (theme.cta_button_text) root.style.setProperty('--wn-cta-text', theme.cta_button_text);
        if (theme.cta_button_image) {
            root.style.setProperty('--wn-cta-bg-image', theme.cta_button_image);
        } else {
            root.style.removeProperty('--wn-cta-bg-image');
        }
        // Feature-video pill — same Solid / gradient-image / text triplet as
        // the trigger pill + CTA. Empty `*_image` ⇒ Solid style, the bundle
        // falls back to the solid --wn-fv-bg colour via the CSS var chain.
        if (theme.feature_video_bg) root.style.setProperty('--wn-fv-bg', theme.feature_video_bg);
        if (theme.feature_video_text) root.style.setProperty('--wn-fv-text', theme.feature_video_text);
        if (theme.feature_video_image) {
            root.style.setProperty('--wn-fv-bg-image', theme.feature_video_image);
        } else {
            root.style.removeProperty('--wn-fv-bg-image');
        }
        if (theme.feature_video_label) featureVideoLabel = theme.feature_video_label;
    }
    function fetchVisibility() {
        if (visibilityFetched) return visibilityFetched;
        visibilityFetched = api('whats_new.api.get_visibility').then(function (data) {
            if (data && typeof data === 'object') {
                visibility.show_on_desk = data.show_on_desk !== false;
                visibility.show_on_frappe_crm = data.show_on_frappe_crm !== false;
                applyTheme(data.theme);
            }
            return visibility;
        }).catch(function () { return visibility; });
        return visibilityFetched;
    }

    function boot() {
        if (!ensureInit()) return;
        fetchVisibility().then(function () {
            if (visibility.show_on_desk) mountTrigger();
        });
    }

    /* ─────────────────────────────────────────────────────────────
     *  Public API — consumers on non-Desk surfaces (CRM, embedded
     *  iframes, custom toolbars) call WhatsNew.mount(target) to drop
     *  the pill exactly where they want it. All mounted pills share
     *  the same unread-badge updates.
     * ───────────────────────────────────────────────────────────── */
    window.WhatsNew = {
        open: function () {
            if (!ensureInit()) return;
            openModal();
        },
        close: function () { closeModal(); },
        mount: function (target) {
            if (!ensureInit()) return null;
            return mountTriggerInto(target);
        },
        refresh: function () { return loadList(); },
    };

    function whenReady(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn, { once: true });
        } else {
            fn();
        }
    }

    whenReady(function () {
        // Wait a tick on Desk so navbar renders first
        if (window.frappe && window.frappe.ready) {
            window.frappe.ready(function () { setTimeout(boot, 300); });
        } else {
            setTimeout(boot, 200);
        }
    });

    // Re-mount on Desk route changes (Desk wipes parts of the DOM on navigation)
    if (window.frappe && window.frappe.router && window.frappe.router.on) {
        window.frappe.router.on('change', function () {
            setTimeout(function () {
                if (!isGuest() && (!triggerEl || !triggerEl.isConnected)) mountTrigger();
            }, 200);
        });
    }
})();
